/* global $ mymuesli */

(function () {

    /**
     * Type identifier / name mapping.
     *
     * @see https://www.mymuesli.com/api/types
     * @type {string[]}
     */
    const types = ['base', 'refine', 'fruits', 'nuts', 'extras'];

    /**
     * Static class for constraint checking functions.
     *
     * @type {{}}
     */
    class ConstraintsChecker {

        /**
         * Checks the maximum amount constraint.
         *
         * @param constraint {{name: string, maximumAmount: string}} The constraint configuration.
         * @param mix mymuesli.entities.Mix The current mix.
         * @param amountChange int The amount change to calculate the new amount.
         */
        static maximumAmount (constraint, mix, amountChange) {

            /* The constraint is only relevant if an ingredient is added. */
            if (amountChange <= 0) {
                return;
            }

            /* Check the constraint for violations. If the constraint is violated, we throw an error. */
            if ((mix.getAmount(this.id) + amountChange) > constraint.maximumAmount) {
                ConstraintsChecker.throwError('ingredient.error.CONSTRAINT_MAXIMUM_AMOUNT', {
                    name: this.name,
                    maximumAmount: constraint.maximumAmount
                });
            }
        }

        /**
         * Checks the exclude constraint.
         *
         * @param constraint {{name: string, excludeIDs: [int]}} The constraint configuration.
         * @param mix mymuesli.entities.Mix The current mix.
         * @param amountChange int The amount change to calculate the new amount.
         */
        static exclude (constraint, mix, amountChange) {

            /* The constraint is only relevant if an ingredient is added. */
            if (amountChange <= 0) {
                return;
            }

            /* Check the constraint for violations. If the constraint is violated, we throw an error. */
            const self = this;
            $.each(constraint.excludeIDs, function (_, excludedID) {
                const excludedIngredient = mix.getIngredient(excludedID);
                const isExcludedBase = (excludedIngredient.isBase && mix.hasBase() && (mix.getBase() === excludedID));
                const isExcludedIngredient = (!excludedIngredient.isBase && (mix.getAmount(excludedID) > 0));
                if ((amountChange > 0) && (isExcludedBase || isExcludedIngredient)) {
                    ConstraintsChecker.throwError('ingredient.error.CONSTRAINT_EXCLUDES', {
                        name: self.name,
                        excludedName: excludedIngredient.name
                    });
                }
            });
        }

        /**
         * Checks the notification constraint.
         *
         * @param constraint {{name: string, text: string, minimumIngredientsAmount: int, maximumIngredientsAmount: int}} The constraint configuration.
         * @param mix mymuesli.entities.Mix The current mix.
         * @param amountChange int The amount change to calculate the new amount.
         */
        static notification (constraint, mix, amountChange) {

            /* Count the ingredients amounts. */
            const ingredientsCountAmount = mix.getRecipeAsList().reduce(function (previousValue, ingredient) {
                return {amount: (previousValue.amount + ingredient.amount)};
            }).amount + amountChange;

            /* If the pre-requisites are matches and the amount of all ingredients is between min and max we display set the notification text. */
            const overMinimum = ((typeof constraint.minimumIngredientsAmount === 'undefined') || (constraint.minimumIngredientsAmount <= ingredientsCountAmount));
            const underMaximum = ((typeof constraint.maximumIngredientsAmount === 'undefined') || (ingredientsCountAmount <= constraint.maximumIngredientsAmount));
            if (overMinimum && underMaximum) {
                 mix.notifications.add(constraint.text);
            } else {
                 mix.notifications.delete(constraint.text);
            }
        }

        /**
         * Throws an localised IngredientError and injects parameters.
         *
         * @param translation The translation identifier.
         * @param parameters The parameters to inject into the message.
         */
        static throwError (translation, parameters) {
            throw new mymuesli.entities.IngredientError(translation, parameters);
        }

    }

    /**
     * A model of an ingredient.
     */
    class Ingredient extends mymuesli.entities.AbstractBase {

        /**
         * Initializes a new Ingredient model.
         *
         * @param plainObject The plain json (optional).
         */
        constructor (plainObject) {
            super({
                id: 0,
                name: '',
                description: '',
                type: 0,
                density: 0.0,
                active: false,
                portionPrice: 0.0,
                portionMilligram: 0,
                isBase: false,
                allergens: [],
                subingredients: [],
                subingredientsString: [],
                typeName: [],
                additionalHints: [],
                featuredAdditionalHints: [],
                mixerConstraints: [],
                nutrition: {
                    saturatedFats: 0.0,
                    fiber: 0.0,
                    sodium: 0.0,
                    energy: 0.0,
                    sugar: 0.0,
                    fats: 0.0,
                    protein: 0.0,
                    carbohydrates: 0.0
                },
                image: {
                    path: '',
                    digest: ''
                },
                icon: {
                    path: '',
                    digest: ''
                },
                flavour: {
                    sweet: 0,
                    sour: 0,
                    savoury: 0,
                    crunchy: 0,
                    creamy: 0
                },
                familiarIngredients: [],
            },
            plainObject);
        }

        /**
         * Gets the name of the current type.
         *
         * @returns {string} The name of the current type.
         */
        get typeName () {
            return types[this.type];
        }

        /**
         * Sets the name of the current type.
         *
         * @param typeName {string} The name of the current type.
         */
        set typeName (typeName) {
            this.type = types.indexOf(typeName);
        }

        /**
         * Checks the ingredient is sold out.
         *
         * @returns {boolean} True if the ingredient is available in the mixer.
         */
        get isActive () {
            return this.active;
        }

        /**
         * Calculates the volume of an ingredient. p = m * V => V = m / p.
         *
         * @see https://en.wikipedia.org/wiki/Density
         * @returns {number} The volume of the ingredient.
         */
        get volume () {
            return this.portionMilligram / this.density;
        }

        /**
         * Returns if the ingredient has an additional hint or not.
         *
         * @param additionalHintID The id of the additonal hint.
         * @return {boolean}
         */
        hasAdditionalHint (additionalHintID) {
            return this.additionalHints.indexOf(parseInt(additionalHintID)) !== -1;
        }

        /**
         * Validate all constraints of the ingredient (maximum amount, includes, excludes).
         *
         * @param mix The current mix entity. This can be used to retrieve further information about other inrgedients.
         * @param amountChange The amount change of the ingredient. The value is optional, default is 0.
         */
        validateConstraints (mix, amountChange = 0) {
            const self = this;
            $.each(this.mixerConstraints, function (_, constraint) {
                ConstraintsChecker[constraint.name].call(self, constraint, mix, amountChange);
            });
        }

    }

    mymuesli.entities.Ingredient = Ingredient;

}) ();
